import LinkifyIt from 'linkify-it';
import { fromEvent, Observable } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import { LinkService } from '../..';
import { createConnectingService } from '../base/connectingService';
import { NULL_SERVICE, Service } from '../base/service';
import { ElementSelectionService } from '../dom/elementSelectionService';
import { ElementService } from '../dom/elementService';

export function createHtmlPasteLinkService(
  element$: Observable<Element | null>,
  selectionService: ElementSelectionService,
  htmlLinkService: LinkService,
): Service {
  return createConnectingService(
    element$,
    () => NULL_SERVICE,
    element => new HtmlPasteLinkService(element, selectionService, htmlLinkService),
  );
}

export class HtmlPasteLinkService extends ElementService<Element> {
  constructor(element: Element, selectionService: ElementSelectionService, htmlLinkService: LinkService) {
    super(element, {
      effects$: fromEvent<ClipboardEvent>(element, 'paste').pipe(
        tap(event => {
          if (event.defaultPrevented) {
            return;
          }

          const { clipboardData } = event;
          if (!clipboardData) {
            return;
          }

          const didAutoLink = this.maybeAutoLink(clipboardData.getData('text/plain'));
          if (didAutoLink) {
            event.preventDefault();
          }
        }),
        share(),
      ),
    });

    this.selectionService = selectionService;
    this.htmlLinkService = htmlLinkService;
    this.linkify = new LinkifyIt({
      fuzzyLink: true,
      fuzzyEmail: true,
    });
  }

  private readonly selectionService: ElementSelectionService;
  private readonly htmlLinkService: LinkService;
  private readonly linkify: LinkifyIt.LinkifyIt;

  public maybeAutoLink(pastedData: string) {
    const matches = this.linkify.match(pastedData);
    // If cannot find URL in clipboard, do nothing.
    if (!matches || matches.length === 0) {
      return false;
    }

    const { url, raw } = matches[0];
    // If clipboard contains no URL, or not only an URL, do nothing.
    if (!url || raw !== pastedData) {
      return false;
    }

    const { selection, selectedText } = this.selectionService;
    // If nothing selected, or only white space selected, do nothing.
    if (!selection || selection.isCollapsed || !selectedText || selectedText.trim() === '') {
      return false;
    }

    this.htmlLinkService.setUrl(url);
    return true;
  }
}
